import React, { useEffect } from "react";
import { Alert, Card, Container, Grid2, Stack, ToggleButton, ToggleButtonGroup, Typography, Skeleton } from "@mui/material";
import { useDebugBorder } from "@core/hooks/useDebugBorder";
import { setSubscriptionPeriod, updateSelectedProduct, useShopSlice, markUnsavedChanges } from "@store/slices/shop";
import { useDispatch } from "react-redux";
import { useQuery } from "@apollo/client";
import { FETCH_SUBSCRIPTION_INFO } from "@company/graphql/queries/fetchSubscriptionInfo";
import { useSubscriptionAccess } from "@company/hooks/useSubscriptionAccess";
import SubscriptionCard, { yearlyDiscount } from "@shop/components/SubscriptionCard";
import { SeatUpgradePreview } from "@shop/components/SeatUpgradePreview";
import { stripePriceToEuro } from "@/utils/format";
import { PAGE_QUOTA_DEFAULTS } from "@/utils/quotaDefaults";
import { State } from "@store/state";
import { useFeatureFlags } from '@/contexts/FeatureFlagContext';

// Generate features dynamically using quota defaults
const features = {
    S: ["ESGbot Chatfunktion", `${PAGE_QUOTA_DEFAULTS.S.toLocaleString('de-DE')} Befragbare Seiten`],
    M: ["ESGbot Chatfunktion", `${PAGE_QUOTA_DEFAULTS.M.toLocaleString('de-DE')} Befragbare Seiten`],
    L: ["ESGbot Chatfunktion", `${PAGE_QUOTA_DEFAULTS.L.toLocaleString('de-DE')} Befragbare Seiten`, "Kundensupport"],
};

const PackageSelection = () => {
    const dispatch = useDispatch();
    const border = useDebugBorder("red");
    const { period, products, selectedProducts } = useShopSlice();

    // Use the enhanced subscription access hook
    const subscriptionAccess = useSubscriptionAccess();

    // Still fetch subscription info for seat details
    const { data: subscriptionData, loading: subscriptionLoading } = useQuery(FETCH_SUBSCRIPTION_INFO, {
        errorPolicy: "ignore",
    });

    const currentSeats = subscriptionData?.subscriptionInfo;

    console.log("🎯 PackageSelection - Enhanced subscription state:", {
        subscriptionAccess,
        subscriptionData,
        subscriptionLoading,
    });

    const onboardingFee = products.find((product: State.Product) => product.name.includes("Onboarding"));
    const { ENABLE_TRIALS } = useFeatureFlags();

    useEffect(() => {
        // Wait for subscription data to load before making decisions about onboarding fee
        if (subscriptionAccess.loading || !onboardingFee) {
            console.log("🔄 PackageSelection: Waiting for subscription data or onboarding fee product...");
            return;
        }

        // Only add onboarding fee for new subscriptions, not for existing ones
        // Use the enhanced hasAccess logic instead of direct status checking
        if (subscriptionAccess.hasAccess) {
            console.log("✅ PackageSelection: User has active subscription access, skipping onboarding fee");
            return;
        }

        // Check if onboarding fee is already in selected products
        const onboardingAlreadySelected = selectedProducts.some((p) => p.priceId === onboardingFee.prices[0].priceId);
        if (onboardingAlreadySelected) {
            console.log("ℹ️ PackageSelection: Onboarding fee already selected");
            return;
        }

        console.log("➕ PackageSelection: Adding onboarding fee for new subscription");
        dispatch(updateSelectedProduct({ priceId: onboardingFee?.prices[0].priceId || "", quantity: 1 }));
    }, [onboardingFee, subscriptionAccess.hasAccess, subscriptionAccess.loading, selectedProducts, dispatch]);

    // Get prices for S, M, L packages by seatType
    const allPrices = products.flatMap((product: State.Product) => product.prices);
    const sPriceData = allPrices.find((price: State.Price) => price.seatType === 'S');
    const mPriceData = allPrices.find((price: State.Price) => price.seatType === 'M');
    const lPriceData = allPrices.find((price: State.Price) => price.seatType === 'L');

    // Debug logging to help troubleshoot pricing issues
    console.log("🔍 PackageSelection - Debug Info:", {
        products,
        allPrices,
        sPriceData,
        mPriceData,
        lPriceData,
        pricesWithSeatTypes: allPrices.filter(p => p.seatType !== null)
    });

    const handleQuantityChange = (quantity: number, priceId: string) => {
        if (!priceId) return;

        dispatch(updateSelectedProduct({ priceId, quantity }));
        // Markiere Änderungen als ungespeichert für Auto-Save
        dispatch(markUnsavedChanges());
    };

    // Get current quantity for a seat type from selected products
    const getCurrentQuantity = (priceId: string) => {
        const selected = selectedProducts.find((p) => p.priceId === priceId);
        return selected?.quantity || 0;
    };

    // Show loading skeletons only during initial load (when products array is empty)
    if (products.length === 0) {
        return (
            <Grid2 container size={12} spacing={4.5} justifyContent={"center"} border={border}>
                <Grid2 container size={12} justifyContent={"center"}>
                    <Skeleton variant="rectangular" width={200} height={40} sx={{ borderRadius: 2 }} />
                </Grid2>

                <Grid2 size={12} container spacing={3} justifyContent={"center"}>
                    <Grid2>
                        <Skeleton variant="rectangular" width={260} height={360} sx={{ borderRadius: 2 }} />
                    </Grid2>
                    <Grid2>
                        <Skeleton variant="rectangular" width={260} height={360} sx={{ borderRadius: 2 }} />
                    </Grid2>
                    <Grid2>
                        <Skeleton variant="rectangular" width={260} height={360} sx={{ borderRadius: 2 }} />
                    </Grid2>
                </Grid2>
            </Grid2>
        );
    }

    return (
        <Grid2 container size={12} spacing={4.5} justifyContent={"center"} border={border}>
            {/* Enhanced subscription status indicator */}
            {subscriptionAccess.isInGracePeriod && (
                <Grid2 size={12}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Ihr Abonnement befindet sich in der Kulanzfrist. Bitte aktualisieren Sie Ihre Zahlungsinformationen.
                    </Alert>
                </Grid2>
            )}

            {/* INTERVAL 📅 Hide monthly/yearly toggle - restore original ToggleButtonGroup below to re-enable monthly */}
            <Grid2 container size={12} justifyContent={"center"} direction="column" alignItems="center" spacing={2}>
                {/* INTERVAL 📅 Toggle completely hidden - uncomment block below to restore monthly/yearly selection
                <Grid2>
                    <ToggleButtonGroup size={"small"} value={period} exclusive onChange={(_, value) => dispatch(setSubscriptionPeriod(value))}>
                        <ToggleButton value={"monthly"}>monatlich</ToggleButton>
                        <ToggleButton value={"yearly"}>{`jährlich (spare ${100 - yearlyDiscount * 100}%)`}</ToggleButton>
                    </ToggleButtonGroup>
                </Grid2>
                */}
            </Grid2>

            {/* Only show onboarding fee for new subscriptions */}
            {!subscriptionAccess.hasAccess && !subscriptionAccess.loading && onboardingFee && (
                <>
                    <Grid2 size={12} mb={-1.5}>
                        <Container maxWidth={"md"}>
                            <Card sx={{ py: 1, px: 2 }}>
                                <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
                                    <Typography variant={"h6"} fontSize={18}>
                                        Onboarding Fee
                                    </Typography>
                                    <Typography>
                                        Einmalige Einrichtungsgebühr - {ENABLE_TRIALS ? 'wird nach der 7-tägigen Testphase abgebucht' : 'wird sofort abgebucht'}
                                    </Typography>
                                    <Typography variant={"h5"} fontSize={16}>
                                        einmalig {stripePriceToEuro(onboardingFee?.prices[0].amount ?? 0)}
                                    </Typography>
                                </Stack>
                            </Card>
                        </Container>
                    </Grid2>
                    <Grid2>
                        <Typography variant={"h6"}>+</Typography>
                    </Grid2>
                </>
            )}

            <Grid2 size={12} container spacing={3} justifyContent={"center"}>
                <Grid2>
                    <SubscriptionCard
                        title={"S"}
                        monthlyPrice={(sPriceData?.amount ?? 0) / 100}
                        priceInterval="year"
                        features={features["S"]}
                        productType="SUBSCRIPTION_SEAT"
                        priceId={sPriceData?.priceId}
                        onQuantityChange={(quantity) => handleQuantityChange(quantity, sPriceData?.priceId || "")}
                        initialQuantity={getCurrentQuantity(sPriceData?.priceId || "")}
                        currentSeats={subscriptionAccess.hasAccess ? currentSeats?.seatsS : undefined}
                        showCurrentSeats={subscriptionAccess.hasAccess}
                    />
                </Grid2>
                <Grid2>
                    <SubscriptionCard
                        title={"M"}
                        monthlyPrice={(mPriceData?.amount ?? 0) / 100}
                        priceInterval="year"
                        features={features["M"]}
                        productType="SUBSCRIPTION_SEAT"
                        isHighlight={true}
                        priceId={mPriceData?.priceId}
                        onQuantityChange={(quantity) => handleQuantityChange(quantity, mPriceData?.priceId || "")}
                        initialQuantity={getCurrentQuantity(mPriceData?.priceId || "")}
                        currentSeats={subscriptionAccess.hasAccess ? currentSeats?.seatsM : undefined}
                        showCurrentSeats={subscriptionAccess.hasAccess}
                    />
                </Grid2>

                <Grid2>
                    <SubscriptionCard
                        title={"L"}
                        monthlyPrice={(lPriceData?.amount ?? 0) / 100}
                        priceInterval="year"
                        features={features["L"]}
                        productType="SUBSCRIPTION_SEAT"
                        priceId={lPriceData?.priceId}
                        onQuantityChange={(quantity) => handleQuantityChange(quantity, lPriceData?.priceId || "")}
                        initialQuantity={getCurrentQuantity(lPriceData?.priceId || "")}
                        currentSeats={subscriptionAccess.hasAccess ? currentSeats?.seatsL : undefined}
                        showCurrentSeats={subscriptionAccess.hasAccess}
                    />
                </Grid2>
            </Grid2>

            {/* Seat Upgrade Preview - only show for existing subscriptions with upgrades */}
            {subscriptionAccess.hasAccess && currentSeats && subscriptionData?.subscriptionInfo?.id && (
                <Grid2 size={12}>
                    <SeatUpgradePreview
                        currentSeats={{
                            S: currentSeats.seatsS || 0,
                            M: currentSeats.seatsM || 0,
                            L: currentSeats.seatsL || 0
                        }}
                        newSeats={{
                            S: getCurrentQuantity(sPriceData?.priceId || ""),
                            M: getCurrentQuantity(mPriceData?.priceId || ""),
                            L: getCurrentQuantity(lPriceData?.priceId || "")
                        }}
                        subscriptionId={subscriptionData.subscriptionInfo.id}
                        isInTrial={subscriptionAccess.isInTrial}
                    />
                </Grid2>
            )}
        </Grid2>
    );
};

export default PackageSelection;
